// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
// Constants
import { ClientAppId, ClientType, Environment } from "@ms/uno-constants/lib/local/AppConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { Store } from "../Store";
import { generateGuid } from "@ms/uno-utilities/lib/local/Guid";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
import { isEqual } from "@ms/uno-utilities/lib/local/ObjectUtilities";
/**
 * Store for app level context data
 */ export class AppContextStore extends EventPublishingStore {
    get name() {
        return Store.AppContext;
    }
    getAppContext() {
        return this.appContext;
    }
    getUserExtraFields() {
        return {
            data: this.userExtraFields,
            status: this.userExtraFieldsEntityStatus
        };
    }
    getAppInstanceId() {
        return this.uuid;
    }
    // This method should be in sync with the one in pex/AppContext isCopilotEnabled
    isCopilotEnabled() {
        return !this.isGovEnvironment() && this.appConfiguration.settings.EnvironmentFeatureSettings.EnableProjectManagerExperience;
    }
    isFullFrameMode() {
        return this.appConfiguration.sessionMetaData.appMetadata.clientType === ClientType.Web;
    }
    getTodoSettings() {
        return {
            data: this.todoSettings,
            status: this.todoSettingsEntityStatus
        };
    }
    isAppInBackground() {
        return this.appInBackground;
    }
    getCurrentUserIdentitySet() {
        if (this.appContext.userId == null || this.appContext.tenantId == null) {
            throw new Error("Cannot retrieve current user identity set while in anonymous mode.");
        }
        return {
            user: {
                id: this.appContext.userId,
                tenantId: this.appContext.tenantId
            },
            application: {
                id: ClientAppId.MicrosoftPlannerClient,
                tenantId: this.appContext.tenantId
            }
        };
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.FetchAppContextSucceeded:
                storeChanged = this.handleFetchAppContext(action);
                break;
            case ActionType.UpdateAppThemeAction:
                storeChanged = this.handleUpdateAppTheme(action);
                break;
            case ActionType.FetchUserExtraFieldsAction:
                storeChanged = this.handleFetchExtraUserFieldsAction();
                break;
            case ActionType.FetchUserExtraFieldsSucceededAction:
                storeChanged = this.handleFetchExtraUserFieldsSucceeded(action);
                break;
            case ActionType.FetchUserExtraFieldsFailedAction:
                storeChanged = this.handleFetchExtraUserFieldsFailed(action);
                break;
            case ActionType.FetchTodoSettingsAction:
                storeChanged = this.handleFetchTodoSettings(action);
                break;
            case ActionType.FetchTodoSettingsFailedAction:
                storeChanged = this.handleFetchTodoSettingsFailed(action);
                break;
            case ActionType.FetchTodoSettingsSucceededAction:
                storeChanged = this.handleFetchTodoSettingsSucceeded(action);
                break;
            case ActionType.UpdateAppBackgroundStateAction:
                storeChanged = this.handleUpdateAppBackgroundStateAction(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                break;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    handleFetchExtraUserFieldsAction() {
        if (!this.userExtraFieldsEntityStatus.isFetching) {
            this.userExtraFieldsEntityStatus.isFetching = true;
            return true;
        }
        return false;
    }
    handleFetchExtraUserFieldsSucceeded(action) {
        this.userExtraFieldsEntityStatus = {
            isFetching: false,
            state: StoreEntityState.Present,
            timestamp: null
        };
        this.userExtraFields = action.userExtraFields;
        return true;
    }
    handleFetchExtraUserFieldsFailed(action) {
        this.userExtraFieldsEntityStatus = {
            isFetching: false,
            state: StoreEntityState.UnableToFetch,
            timestamp: null
        };
        return true;
    }
    handleFetchAppContext(action) {
        if (!isEqual(this.appContext, action.appContext)) {
            this.appContext = action.appContext;
            return true;
        }
        return false;
    }
    handleUpdateAppTheme(action) {
        if (this.appContext.theme !== action.theme) {
            this.appContext = {
                ...this.appContext,
                theme: action.theme
            };
            return true;
        }
        return false;
    }
    handleFetchTodoSettings(action) {
        if (!this.todoSettingsEntityStatus.isFetching) {
            this.todoSettingsEntityStatus.isFetching = true;
            return true;
        }
        return false;
    }
    handleFetchTodoSettingsFailed(action) {
        this.todoSettingsEntityStatus = {
            isFetching: false,
            state: StoreEntityState.UnableToFetch,
            timestamp: null
        };
        return true;
    }
    handleFetchTodoSettingsSucceeded(action) {
        this.todoSettings = action.settings;
        this.todoSettingsEntityStatus = {
            isFetching: false,
            state: StoreEntityState.Present,
            timestamp: null
        };
        return true;
    }
    handleUpdateAppBackgroundStateAction(action) {
        if (this.appInBackground !== action.isAppInBackground) {
            this.appInBackground = action.isAppInBackground;
            return true;
        }
        return false;
    }
    isGovEnvironment() {
        return this.appConfiguration.sessionMetaData.environment === Environment.Gcc || this.appConfiguration.sessionMetaData.environment === Environment.Gcch || this.appConfiguration.sessionMetaData.environment === Environment.Dod || this.appConfiguration.sessionMetaData.environment === Environment.Agc;
    }
    constructor(initialContext, appConfiguration, loggers){
        super(), this.loggers = loggers, this.userExtraFields = null, this.todoSettings = null, this.userExtraFieldsEntityStatus = {
            isFetching: false,
            state: StoreEntityState.Absent,
            timestamp: null
        }, this.todoSettingsEntityStatus = {
            isFetching: false,
            state: StoreEntityState.Absent,
            timestamp: null
        };
        this.appContext = initialContext;
        this.appConfiguration = appConfiguration;
        this.appInBackground = initialContext.isBackgroundLoad;
        this.uuid = generateGuid();
    }
}
